import React, { useEffect, useState } from 'react';
import { Box, Flex, Text, useToast, useTheme } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import FormHeader from '../../components/FormHeader/FormHeader.tsx';
import {
  overviewContainerStyles,
  overviewItemBoxStyles,
  overviewItemTitleStyles,
  overviewItemValueStyles,
  overviewSectionContainerStyles,
  overviewSectionTitleContainerStyles,
  overviewSectionTitleStyles,
} from '../../styles/ts/overviewStyles.ts';
import { useTranslation } from 'react-i18next';
import AleaToggle from '../../components/AleaToggle/AleaToggle.tsx';
import { BLACK_COLOR, GRAY_COLOR } from '../../constants/colors.ts';
import AleaEditButton from '../../components/Buttons/AleaEditButton/AleaEditButton.tsx';
import BedsoreSelection, {
  BedSoreSelections,
} from '../../components/Resident/BedsoreSelection/BedsoreSelection.tsx';
import bodyRegions from '../../components/Resident/BedsoreSelection/region_mapping.json';
import ResidentService from '../../services/ResidentService.ts';
import StationService from '../../services/StationService.ts';
import CareHomeService from '../../services/CareHomeService.ts';
import { IResidentListItem } from '../../interfaces/Resident/IResidentListItem.ts';
import { StatusType } from '../../constants/statusColors.ts';
import StatusBox from '../../components/StatusBox/StatusBox.tsx';
import { ICareHome } from '../../interfaces/CareHome/ICareHome.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { IResidentUpdatePayload } from '../../interfaces/Resident/IResidentUpdatePayload.ts';
import { ResidentFeature } from '../../constants/ResidentFeature.ts';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import { getLanguageKeyByType } from '../../utils/getLanguageKeyByType.ts';
import formatTimeToHours from '../../utils/formatTimeToHours.ts';
import { getPositionTranslationKey } from '../../utils/getPositionTranslationKey.ts';
import AleaArchiveIcon from '../../icons/AleaArchiveIcon.tsx';
import AleaRestoreIcon from '../../icons/AleaRestoreIcon.tsx';
import AleaModal from '../../components/AleaModal/AleaModal.tsx';
import { getDiaperUsageTranslationKey } from '../../constants/DiaperUsageEnum.ts';
import { UserRole } from '../../constants/UserRoles.ts';
import { hasFeaturePermission } from '../../utils/featurePermission.ts';
import AleaStatusModal from '../../modals/AleaStatusModal/AleaStatusModal.tsx';
import AleaInfoModal from '../../modals/AleaInfoModal/AleaInfoModal.tsx';

const ResidentOverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const theme = useTheme();

  const [resident, setResident] = useState<IResidentListItem | null>(null);
  const [careHome, setCareHome] = useState<ICareHome | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFeatureRequestLoading, setIsFeatureRequestLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stationName, setStationName] = useState<string>('');
  
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string>('');

  const [isSafeZoneVisible, setIsSafeZoneVisible] = useState(false);
  const [isFallDetectionVisible, setIsFallDetectionVisible] = useState(false);
  const [isIncontinenceManagementVisible, setIsIncontinenceManagementVisible] =
    useState(false);
  const [isBedsoresPreventionVisible, setIsBedsoresPreventionVisible] =
    React.useState<boolean>(false);
  const [selections, setSelections] = React.useState<BedSoreSelections>({});
  const [bedSoresList, setBedSoresList] = React.useState<
    Array<{ region: string; risk: string; note: string }>
  >([]);

  // CareHome data fetch
  const fetchCareHomeData = async () => {
    try {
      if (user?.carehome?.id) {
        const response = await CareHomeService.getCareHome(user.carehome.id);
        setCareHome(response);
        return response;
      }
    } catch (error) {
      setError('Failed to fetch care home data');
      throw error;
    }
  };

  // Resident data fetch
  const fetchResidentData = async (id: string) => {
    try {
      const response = await ResidentService.getResident(id);
      setResident(response);

      if (response.features) {
        setIsIncontinenceManagementVisible(
          response.features.includes('Incontinence Management (Urine)')
        );
        setIsBedsoresPreventionVisible(
          response.features.includes('Repositioning Management')
        );
        setIsSafeZoneVisible(response.features.includes('Secure Zone'));
        setIsFallDetectionVisible(
          response.features.includes('Fall Management')
        );
      }

      return response;
    } catch (error) {
      setError('Failed to fetch resident data');
      throw error;
    }
  };

  // Station data fetch
  const fetchStationData = async (stationId: string) => {
    try {
      const response = await StationService.getStation(stationId);
      setStationName(response.name || '-');
    } catch (error) {
      setError('Failed to fetch station data');
    }
  };

  // Gateway data fetch
  

  const updateResidentStatus = async (newStatus: StatusType) => {
    if (resident) {
      const payload: IResidentUpdatePayload = {
        carehome_id: user?.carehome?.id || '',
        station_id: resident.station.id,
        room: resident.room,
        first_name: resident.first_name,
        middle_name: resident.middle_name || null,
        last_name: resident.last_name,
        weight: resident.weight || null,
        height: resident.height || null,
        gender: resident.gender,
        date_of_birth: resident.dob || null,
        state: newStatus,
        features: resident.features || null,
        is_active: true,
      };

      try {
        await ResidentService.updateResident(resident.id, payload);
        setResident({ ...resident, state: newStatus });
        // Show info modal on success
        setInfoMessage(t('statusAdapted'));
        setIsInfoModalOpen(true);
        setTimeout(() => setIsInfoModalOpen(false), 1800);
        return Promise.resolve();
      } catch (error) {
        console.error('Failed to update resident status:', error);
        return Promise.reject(error);
      }
    }
    return Promise.reject(new Error('No resident data available'));
  };

  const fetchBedSores = async (residentId: string) => {
    try {
      const bedsores = await ResidentService.getResidentBedsores(residentId);
      // Normalize to array whether API returns array or { items: [...] }
      const list: any[] = Array.isArray(bedsores)
        ? (bedsores as any[])
        : ((bedsores as any)?.items || []);
      if (list && Array.isArray(list)) {
        // Map label -> key fallback if API ever returns labels
        const reverseMap: Record<string, string> = Object.entries(bodyRegions as any).reduce((acc, [key, val]) => {
          acc[val as string] = key;
          return acc;
        }, {} as Record<string, string>);

        const newSelections = list.reduce(
          (acc: BedSoreSelections, bedsore: any) => {
            const idKey = reverseMap[bedsore.region] || bedsore.region;
            acc[idKey] = {
              risk: bedsore.risk,
              note: bedsore.note || '',
            };
            return acc;
          },
          {}
        );
        setSelections(newSelections);

        // Set bed sores list for display
        setBedSoresList(
          list.map((item: any) => ({
            region: item.region,
            risk: item.risk,
            note: item.note || '',
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch care home data first
        await fetchCareHomeData();
        
        const residentResponse = await fetchResidentData(String(id));

        if (residentResponse.station?.id) {
          await fetchStationData(residentResponse.station.id);
        } else {
          setStationName('-');
        }

        // Hardware section removed
      } catch (error) {
        // Handle errors
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (resident) {
      fetchBedSores(resident?.id);
    }
  }, [resident]);

  if (isLoading) {
    return <AleaLoader />;
  }

  if (error) {
    return <Box>{error}</Box>;
  }

  if (!resident) {
    return <Box>No resident data found</Box>;
  }

  /**
   * Navigates to the edit page for the resident, setting the specified tab index and
   * retaining the return path for navigation.
   *
   * @param {number} tabIndex - The index of the tab to be activated on the edit page.
   */
  const handleOverviewEdit = (tabIndex: number) => {
    navigate(`/residents/edit/${id}?tab=${tabIndex}`, {
      state: { from: `/residents/${id}` },
    });
  };

  const renderValue = (value: string | number | null | undefined) =>
    value !== null && value !== undefined && value !== '' ? value : '-';

  const handleFeatureToggle = async (
    feature: ResidentFeature,
    isChecked: boolean
  ) => {
    if (!resident) return;

    const updatedFeatures = new Set(resident.features);

    if (isChecked) {
      updatedFeatures.add(feature);
    } else {
      updatedFeatures.delete(feature);
    }

    const featureArray = Array.from(updatedFeatures);

    try {
      setIsFeatureRequestLoading(true);
      await ResidentService.updateResidentFeatures(resident.id, featureArray);
      setResident({ ...resident, features: featureArray });
      toast({
        title: t('success'),
        description: t('resident.featuresUpdated'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsFeatureRequestLoading(false);
    }
  };

  // Handle Archive button click
  const handleArchiveClick = () => {
    setIsArchiveModalOpen(true);
  };

  // Handle Archive Confirmation
  const handleArchiveConfirm = async () => {
    console.log('Archive confirmed for resident:', resident?.id);

    if (resident) {
      setIsFeatureRequestLoading(true);

      try {
        // Update resident state to Archived
        await updateResidentStatus('Archived');

        toast({
          title: t('success'),
          description: t('resident.archivedSuccessfully', {
            name: `${resident?.first_name} ${resident?.last_name}`,
          }),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error archiving resident:', error);

        toast({
          title: t('error'),
          description: t('resident.archiveError'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsFeatureRequestLoading(false);
        setIsArchiveModalOpen(false);
      }
    } else {
      setIsArchiveModalOpen(false);
    }
  };

  // Handle Restore button click
  const handleRestoreClick = () => {
    setIsRestoreModalOpen(true);
  };

  // Handle Restore Confirmation
  const handleRestoreConfirm = async () => {
    console.log('Restore confirmed for resident:', resident?.id);

    if (resident) {
      setIsFeatureRequestLoading(true);

      try {
        // Update resident state to Enabled
        await updateResidentStatus('Enabled');

        toast({
          title: t('success'),
          description: t('resident.restoredSuccessfully'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error restoring resident:', error);

        toast({
          title: t('error'),
          description: t('resident.restoreError'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsFeatureRequestLoading(false);
        setIsRestoreModalOpen(false);
      }
    } else {
      setIsRestoreModalOpen(false);
    }
  };

  const editButtonEnabled =
    user?.role !== UserRole.ORGANISATION_MANAGER &&
    resident?.state !== 'Archived';

  return (
    <Box data-testid="resident-detail-page">
      {/*main title*/}
      <FormHeader
        title={`${resident.first_name} ${resident.last_name}`}
        redirectUrl="/residents"
        actionButton={
          user?.role !== UserRole.ORGANISATION_MANAGER
            ? resident?.state === 'Archived'
              ? {
                  icon: <AleaRestoreIcon size="24" />,
                  label: t('resident.restore'),
                  onClick: handleRestoreClick,
                  isRestore: true,
                }
              : {
                  icon: <AleaArchiveIcon size="21" />,
                  label: t('resident.archive'),
                  onClick: handleArchiveClick,
                }
            : undefined
        }
      />

      <Flex sx={overviewContainerStyles}>
        {/* status section (read-only) */}
        <Box sx={overviewSectionContainerStyles}>
          <Box maxWidth="890px">
            {/*section title*/}
            <Flex sx={overviewSectionTitleContainerStyles}>
              <Flex alignItems="center">
                <Text sx={overviewSectionTitleStyles}>{t('status')}</Text>
                {editButtonEnabled && (
                  <AleaEditButton
                    onClick={() => setIsStatusModalOpen(true)}
                    label={t('edit')}
                  />
                )}
              </Flex>
            </Flex>

            <Box>
              <StatusBox status={resident.state as StatusType} showText textColor="#000" />
            </Box>
          </Box>
        </Box>

        {/*personal data*/}
        <Box sx={overviewSectionContainerStyles}>
          <Box maxWidth="890px">
            {/*section title*/}
            <Flex sx={overviewSectionTitleContainerStyles}>
              <Flex alignItems="center">
                <Text sx={overviewSectionTitleStyles}>{t('personalData')}</Text>
                {editButtonEnabled && (
                  <AleaEditButton
                    onClick={() => handleOverviewEdit(0)}
                    label={t('edit')}
                  />
                )}
              </Flex>
            </Flex>

            <Box>
              <Flex flexWrap="wrap" gap="15px">
                {/*firstName*/}
                <Box sx={overviewItemBoxStyles}>
                  <Text sx={overviewItemTitleStyles}>{t('firstName')}</Text>
                  <Text sx={overviewItemValueStyles}>
                    {renderValue(resident.first_name)}
                  </Text>
                </Box>

                {/*lastName*/}
                <Box sx={overviewItemBoxStyles}>
                  <Text sx={overviewItemTitleStyles}>{t('lastName')}</Text>
                  <Text sx={overviewItemValueStyles}>
                    {renderValue(resident.last_name)}
                  </Text>
                </Box>

                {/*weight*/}
                <Box sx={overviewItemBoxStyles}>
                  <Text sx={overviewItemTitleStyles}>{t('weight')}</Text>
                  <Text sx={overviewItemValueStyles}>
                    {renderValue(resident.weight)}
                  </Text>
                </Box>

                {/*height*/}
                <Box sx={overviewItemBoxStyles}>
                  <Text sx={overviewItemTitleStyles}>{t('height')}</Text>
                  <Text sx={overviewItemValueStyles}>
                    {renderValue(resident.height)}
                  </Text>
                </Box>

                {/*gender*/}
                <Box sx={overviewItemBoxStyles}>
                  <Text sx={overviewItemTitleStyles}>{t('gender')}</Text>
                  <Text sx={overviewItemValueStyles}>
                    {renderValue(t(resident.gender?.toLowerCase()))}
                  </Text>
                </Box>

                {/*livingArea*/}
                <Box sx={overviewItemBoxStyles}>
                  <Text sx={overviewItemTitleStyles}>
                    {t(
                      getLanguageKeyByType('livingArea', user?.carehome?.type)
                    )}
                  </Text>
                  <Text sx={overviewItemValueStyles}>
                    {renderValue(stationName)}
                  </Text>
                </Box>

                {/*room*/}
                <Box sx={overviewItemBoxStyles}>
                  <Text sx={overviewItemTitleStyles}>{t('room')}</Text>
                  <Text sx={overviewItemValueStyles}>
                    {renderValue(resident.room)}
                  </Text>
                </Box>
              </Flex>
            </Box>
          </Box>
        </Box>

        {/*incontinence management*/}
        <Box sx={overviewSectionContainerStyles}>
          <Box maxWidth="890px">
            {/*section title*/}
            <Flex sx={overviewSectionTitleContainerStyles}>
              <Flex alignItems="center">
                <Text
                  sx={overviewSectionTitleStyles}
                  color={
                    isIncontinenceManagementVisible ? BLACK_COLOR : GRAY_COLOR
                  }
                >
                  {t('incontinenceManagement')}
                </Text>
                {isIncontinenceManagementVisible && editButtonEnabled && hasFeaturePermission(careHome, ResidentFeature.INCONTINENCE_MANAGEMENT_URINE) && (
                  <AleaEditButton
                    onClick={() => handleOverviewEdit(2)}
                    label={t('edit')}
                  />
                )}
              </Flex>
            </Flex>

            {isIncontinenceManagementVisible && (
              <>
              <Box>
                {/* 1) Urinerkennung toggle */}
                <Flex justifyContent="space-between" mb="25px">
                  <Text sx={overviewItemTitleStyles}>{t('Incontinence Management (Urine)')}</Text>
                  <Flex minWidth="190px">
                    <AleaToggle
                      isChecked={true}
                      onToggle={() => {}}
                      title={t('activated')}
                      offTitle={t('activated')}
                      disabled={true}
                    />
                  </Flex>
                </Flex>

                <Flex mb="25px" flexWrap="wrap" gap="15px">
                  {/*ICPType*/}
                  <Box sx={overviewItemBoxStyles}>
                    <Text sx={overviewItemTitleStyles}>{t('ICPType')}</Text>
                    <Box sx={overviewItemValueStyles}>
                      {resident.diapers.map((diaper) => (
                        <Box key={diaper.id}>
                          {renderValue(diaper.diaper.name)}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/*timeOfDay*/}
                  <Box sx={overviewItemBoxStyles}>
                    <Text sx={overviewItemTitleStyles}>{t('timeOfDay')}</Text>
                    <Box sx={overviewItemValueStyles}>
                      {resident.diapers.map((diaper) => (
                        <Box key={diaper.id}>
                          {renderValue(
                            t(getDiaperUsageTranslationKey(diaper.usage))
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Flex>
              </Box>
              {Boolean(resident?.has_catheter) && (
                <Box>
                  <Text fontSize="15px" color="alea.500" fontWeight={300}>
                    {t('residentWearsCatheter', { name: `${resident.first_name} ${resident.last_name}` })}
                  </Text>
                </Box>
              )}
              </>
            )}
          </Box>
        </Box>

        {/*bedsores prevention*/}
        <Box sx={overviewSectionContainerStyles}>
          <Box maxWidth="890px">
            {/*section title*/}
            <Flex sx={overviewSectionTitleContainerStyles}>
              <Flex alignItems="center">
                <Text
                  sx={overviewSectionTitleStyles}
                  color={isBedsoresPreventionVisible ? BLACK_COLOR : GRAY_COLOR}
                >
                  {t('bedsoresPrevention')}
                </Text>
                {isBedsoresPreventionVisible && editButtonEnabled && hasFeaturePermission(careHome, ResidentFeature.REPOSITIONING_MANAGEMENT) && (
                  <AleaEditButton
                    onClick={() => handleOverviewEdit(3)}
                    label={t('edit')}
                  />
                )}
              </Flex>
              <Flex minWidth="190px">
                <AleaToggle
                  isChecked={isBedsoresPreventionVisible}
                  onToggle={(e) => {
                    setIsBedsoresPreventionVisible(e.target.checked);
                    handleFeatureToggle(
                      ResidentFeature.REPOSITIONING_MANAGEMENT,
                      e.target.checked
                    );
                  }}
                  title={t('activated')}
                  offTitle={t('deactivated')}
                  disabled={!editButtonEnabled || !hasFeaturePermission(careHome, ResidentFeature.REPOSITIONING_MANAGEMENT)}
                />
              </Flex>
            </Flex>

            {isBedsoresPreventionVisible && (
              <Box>
                <Flex mb="25px" gap="30px">
                  {/* left */}
                  <Box w="400px" gap="15px">
                    <Box mb="30px">
                      {/* Allgemeine Lagerungsintervalle (General Day) */}
                      {resident?.repos_intervals?.some((i) => i.type === 'General Day') && (
                        <Box w="100%">
                          <Text sx={overviewItemTitleStyles}>Allgemeine Lagerungsintervalle</Text>
                          <Box sx={overviewItemValueStyles}>
                            {resident.repos_intervals
                              .filter((interval) => interval.type === 'General Day')
                              .map((interval, idx) => {
                                const toTimeDisplay = (time: string) => {
                                  const [hh = '00', mm = '00'] = (time || '00:00:00').split(':');
                                  return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
                                };
                                const value = interval.value || '00:00:00';
                                const [vh, vm] = value.split(':').map((v) => parseInt(v || '0', 10));
                                const hoursValue = vh + (vm === 30 ? 0.5 : 0);
                                const rightText =
                                  value === '00:00:00'
                                    ? t('noReposInterval')
                                    : t(`every${hoursValue}h`, { keySeparator: false });

                                return (
                                  <Flex key={idx} w="100%" justifyContent="space-between" flexWrap="wrap">
                                    <Box fontWeight={400} width="50%">
                                      {`${toTimeDisplay(interval.start || '00:00:00')} - ${toTimeDisplay(interval.end || '00:00:00')}`}
                                    </Box>
                                    <Box fontWeight={400} width="50%" textAlign="left">{rightText}</Box>
                                  </Flex>
                                );
                              })}
                          </Box>
                        </Box>
                      )}                                        
                    </Box>

                    <Box w="100%" mb="25px">
                      {/*positions and maxInterval*/}
                      {resident.repos_intervals &&
                        resident.repos_intervals.length > 0 && (
                          <>
                            <Box w="100%">
                              <Text sx={overviewItemTitleStyles}>
                                {t('defineReposIntervalBodyPartsHeadline')}
                              </Text>

                              <Flex justifyContent="space-between" w="100%">
                                <Box sx={overviewItemValueStyles} w="50%">
                                  {resident?.repos_intervals?.map(
                                    (interval, index) => (
                                      <Box key={index} fontWeight={400}>
                                        {t(
                                          getPositionTranslationKey(interval.type)
                                        )}
                                      </Box>
                                    )
                                  )}
                                </Box>
                                <Box w="50%">
                                  <Box sx={overviewItemValueStyles}>
                                    {resident?.repos_intervals?.map(
                                      (interval, index) => (
                                        <Box key={index} fontWeight={400}>
                                          {formatTimeToHours(interval.value)}
                                        </Box>
                                      )
                                    )}
                                  </Box>
                              </Box>                                
                              </Flex>                           
                            </Box>
                          </>
                        )}                                        
                    </Box>

                    <Box w="100%" mb="25px">
                      {/*Dekubitus-Risiko*/}
                      <Box w="100%">
                        <Flex justifyContent="space-between" w="100%">
                          <Box sx={overviewItemValueStyles} w="50%">
                            <Text sx={overviewItemTitleStyles}>
                              {t('bedsoresRisk')}
                            </Text>
                            <Box fontWeight={400}>
                              {bedSoresList.filter((i) => (i.risk || '').toLowerCase() === 'threat').length > 0 ? (
                                bedSoresList
                                  .filter((i) => (i.risk || '').toLowerCase() === 'threat')
                                  .map((item, idx) => (
                                    <Box key={idx}>
                                      {bodyRegions[item.region as keyof typeof bodyRegions] || item.region}
                                    </Box>
                                  ))
                              ) : (
                                <Box>-</Box>
                              )}
                            </Box>
                          </Box>
                          <Box w="50%">
                            <Text sx={overviewItemTitleStyles}>
                              {t('notes')}
                            </Text>
                            <Box sx={overviewItemValueStyles}>
                              {bedSoresList.filter((i) => (i.risk || '').toLowerCase() === 'threat').length > 0 ? (
                                bedSoresList
                                  .filter((i) => (i.risk || '').toLowerCase() === 'threat')
                                  .map((item, idx) => (
                                    <Box key={idx} fontWeight={400}>
                                      {item.note && item.note.trim().length > 0 ? item.note : '-'}
                                    </Box>
                                  ))
                              ) : (
                                <Box fontWeight={400}>-</Box>
                              )}
                            </Box>
                          </Box>
                        </Flex>
                      </Box>
                    </Box>        

                    <Box w="100%">
                      {/*Wunde*/}
                      <Box w="100%">
                        <Flex justifyContent="space-between" w="100%">
                          <Box sx={overviewItemValueStyles} w="50%">
                            <Text sx={overviewItemTitleStyles}>
                              {t('wound')}
                            </Text>
                            <Box fontWeight={400}>
                              {bedSoresList.filter((i) => (i.risk || '').toLowerCase() === 'wound').length > 0 ? (
                                bedSoresList
                                  .filter((i) => (i.risk || '').toLowerCase() === 'wound')
                                  .map((item, idx) => (
                                    <Box key={idx}>
                                      {bodyRegions[item.region as keyof typeof bodyRegions] || item.region}
                                    </Box>
                                  ))
                              ) : (
                                <Box>-</Box>
                              )}
                            </Box>
                          </Box>
                          <Box w="50%">
                            <Text sx={overviewItemTitleStyles}>
                              {t('notes')}
                            </Text>
                            <Box sx={overviewItemValueStyles}>
                              {bedSoresList.filter((i) => (i.risk || '').toLowerCase() === 'wound').length > 0 ? (
                                bedSoresList
                                  .filter((i) => (i.risk || '').toLowerCase() === 'wound')
                                  .map((item, idx) => (
                                    <Box key={idx} fontWeight={400}>
                                      {item.note && item.note.trim().length > 0 ? item.note : '-'}
                                    </Box>
                                  ))
                              ) : (
                                <Box fontWeight={400}>-</Box>
                              )}
                            </Box>
                          </Box>
                        </Flex>
                      </Box>
                    </Box>        
                  </Box>

                  {/* right */}
                  <Flex w="437.5px" flexWrap="wrap" gap="15px">
                    {/*pronePosition*/}
                    <Box sx={overviewItemBoxStyles}>
                      <Text sx={overviewItemTitleStyles} mb="15px">
                        {t('pronePosition')}
                      </Text>
                      <Box sx={overviewItemValueStyles}>
                        <BedsoreSelection
                          disableTooltips={true}
                          title={''}
                          gender="female"
                          view="front"
                          tooltipPlacement="right"
                          existingSelections={selections}
                        />
                      </Box>
                    </Box>

                    {/*supinePosition*/}
                    <Box sx={overviewItemBoxStyles}>
                      <Text sx={overviewItemTitleStyles} mb="15px">
                        {t('supinePosition')}
                      </Text>
                      <Box sx={overviewItemValueStyles}>
                        <BedsoreSelection
                          disableTooltips={true}
                          title={''}
                          gender="female"
                          view="back"
                          tooltipPlacement="right"
                          existingSelections={selections}
                        />
                      </Box>
                    </Box>
                  </Flex>
                </Flex>
              </Box>
            )}
          </Box>
        </Box>

        {/*safe zone*/}
        <Box sx={overviewSectionContainerStyles}>
          <Box maxWidth="890px">
            {/*section title*/}
            <Flex sx={overviewSectionTitleContainerStyles}>
              <Flex>
                <Text
                  sx={overviewSectionTitleStyles}
                  color={isSafeZoneVisible ? BLACK_COLOR : GRAY_COLOR}
                >
                  {t('safeZone')}
                </Text>
              </Flex>
              <Flex minWidth="190px">
                <AleaToggle
                  isChecked={isSafeZoneVisible}
                  onToggle={(e) => {
                    setIsSafeZoneVisible(e.target.checked);
                    handleFeatureToggle(
                      ResidentFeature.SECURE_ZONE,
                      e.target.checked
                    );
                  }}
                  title={t('activated')}
                  offTitle={t('deactivated')}
                  disabled={!editButtonEnabled || !hasFeaturePermission(careHome, ResidentFeature.SECURE_ZONE)}
                />
              </Flex>
            </Flex>
          </Box>
        </Box>

        {/*fall detection*/}
        <Box sx={overviewSectionContainerStyles}>
          <Box maxWidth="890px">
            {/*section title*/}
            <Flex sx={{ ...overviewSectionTitleContainerStyles, mb: 0 }}>
              <Flex>
                <Text
                  sx={overviewSectionTitleStyles}
                  color={isFallDetectionVisible ? BLACK_COLOR : GRAY_COLOR}
                >
                  {t('fallDetection')}
                </Text>
              </Flex>
              <Flex minWidth="190px">
                <AleaToggle
                  isChecked={isFallDetectionVisible}
                  onToggle={(e) => {
                    setIsFallDetectionVisible(e.target.checked);
                    handleFeatureToggle(
                      ResidentFeature.FALL_MANAGEMENT,
                      e.target.checked
                    );
                  }}
                  title={t('activated')}
                  offTitle={t('deactivated')}
                  disabled={!editButtonEnabled || !hasFeaturePermission(careHome, ResidentFeature.FALL_MANAGEMENT)}
                />
              </Flex>
            </Flex>
          </Box>
        </Box>        
      </Flex>
      {/* Status Edit Modal */}
      <AleaStatusModal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        residentName={`${resident.first_name} ${resident.last_name}`}
        currentStatus={resident.state as StatusType}
        onConfirm={async (newStatus) => {
          await updateResidentStatus(newStatus);
          setIsStatusModalOpen(false);
        }}
      />
      {/* Info Modal */}
      <AleaInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        message={infoMessage || t('statusAdapted')}
      />
      {/* Archive Confirmation Modal */}
      <AleaModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        title={t('resident.archiveConfirmNamed', {
          name: `${resident?.first_name} ${resident?.last_name}`,
        })}
        onConfirm={handleArchiveConfirm}
        confirmLabel={t('confirm')}
        cancelLabel={t('cancel')}
        variant="confirm"
        titleColor="sunsetEmber.500"
      >
        <Box px="34px">
          <Text
            fontSize="18px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="normal"
          >
            {t('resident.undoableInfo')}
          </Text>
        </Box>
      </AleaModal>
      {/* Restore Confirmation Modal */}
      <AleaModal
        isOpen={isRestoreModalOpen}
        onClose={() => setIsRestoreModalOpen(false)}
        title={t('resident.restoreConfirmNamed', {
          name: `${resident?.first_name} ${resident?.last_name}`,
        })}
        onConfirm={handleRestoreConfirm}
        confirmLabel={t('confirm')}
        cancelLabel={t('cancel')}
        variant="confirm"
        titleColor={theme.colors.alea[500]}
      >
        <Box px="34px">
          <Text
            fontSize="18px"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="normal"
          >
            {t('resident.restoreConfirm')}
          </Text>
        </Box>
      </AleaModal>
      {isFeatureRequestLoading && <AleaLoader />} {/* Loader */}
    </Box>
  );
};

export default ResidentOverviewPage;
